import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ExternalLink } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import { destinationOf } from "@/lib/alertTarget";
import { addDays, isoDate, parseISO } from "@/lib/eventMeta";

const tomorrowOf = (date) => isoDate(addDays(parseISO(date), 1));

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [list, count] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setItems(list.data);
      setUnread(count.data.unread);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 120000);
    window.addEventListener("alerts-changed", load);
    return () => {
      clearInterval(id);
      window.removeEventListener("alerts-changed", load);
    };
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  const openAlert = async (n) => {
    const target = destinationOf(n);
    if (!n.read) {
      setItems((list) =>
        list.map((i) => (i.notification_id === n.notification_id ? { ...i, read: true } : i)),
      );
      setUnread((u) => Math.max(0, u - 1));
      api.post(`/notifications/${n.notification_id}/read`).catch(() => {});
    }
    if (!target) return;
    setOpen(false);
    if (target.kind === "external") window.open(target.to, "_blank", "noopener");
    else navigate(target.to);
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) load();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative h-10 w-10 grid place-items-center rounded-sm border border-border hover:bg-accent transition-colors duration-200"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 ? (
            <span
              data-testid="notification-unread-badge"
              className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[0.65rem] font-bold"
            >
              {unread}
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="label-caps">Alerts</span>
          <button
            data-testid="mark-all-read-btn"
            onClick={markAll}
            className="text-xs font-medium hover:underline"
          >
            Mark all read
          </button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {items.length ? (
          items.map((n) => {
            const target = destinationOf(n);
            return (
              <div
                key={n.notification_id}
                data-testid={`notification-${n.notification_id}`}
                className={`border-b border-border last:border-0 ${n.read ? "opacity-60" : ""}`}
              >
                <button
                  data-testid={`notification-link-${n.notification_id}`}
                  onClick={() => openAlert(n)}
                  className={`w-full text-left px-3 py-3 transition-colors duration-200 ${
                    target ? "hover:bg-accent/60 cursor-pointer" : "cursor-default"
                  }`}
                >
                  <p className="text-sm font-semibold leading-snug">{n.title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{n.body}</p>
                  {target ? (
                    <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold underline">
                      {target.label}
                      {target.kind === "external" ? <ExternalLink className="h-3 w-3" /> : null}
                    </span>
                  ) : null}
                </button>
                {n.kind?.startsWith("todo_rollover") && n.date ? (
                  <button
                    data-testid={`notification-rollover-${n.notification_id}`}
                    onClick={() => {
                      setOpen(false);
                      navigate(`/todos?date=${tomorrowOf(n.date)}&rollover=${n.date}`);
                    }}
                    className="px-3 pb-3 text-xs font-semibold underline"
                  >
                    Review and roll over
                  </button>
                ) : null}
              </div>
            );
          })
        ) : (
          <p className="px-3 py-6 text-sm text-muted-foreground">No alerts yet.</p>
        )}
        <DropdownMenuSeparator />
        <button
          data-testid="alerts-see-all"
          onClick={() => {
            setOpen(false);
            navigate("/alerts");
          }}
          className="w-full px-3 py-3 text-xs font-semibold uppercase tracking-wider hover:bg-accent/60 transition-colors duration-200"
        >
          See every alert
        </button>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
